/**
 * Whether the signed-in person may decide certification reimbursements, and how they got the right.
 *
 * ── One question, asked in several places ─────────────────────────────────────────────────
 *
 * The approvals page, the sidebar and the row-level actions all need the same answer, and it
 * depends on a configuration value that has to be fetched. Asking each of them to fetch it would
 * mean three reads and three chances to disagree about who may approve — which, on a screen with
 * Approve and Reject buttons, is the kind of disagreement that gets noticed only after somebody
 * has been shown a control that then fails.
 *
 * `via` is returned alongside the boolean because the two grants are not interchangeable on
 * screen. An existing administrator reaches certifications through the admin area they already
 * use; the configured Certification Admin has certification approval and NOTHING else, so their
 * page has to be self-contained rather than a link into an admin dashboard they cannot open.
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types/user';
import {
  canApproveFinance,
  canViewCertificateAdminApproval,
  canViewCertificationApprovals,
  canViewFinanceReimbursements,
} from '../utils/rolePermissions';
import { loadCertificationSettings } from '../services/certificationSettings';

export interface CertificationApprovalAccess {
  /** True when this person may give the ADMIN decision on a certification reimbursement. */
  canApprove: boolean;
  /**
   * The three stage gates, evaluated independently and combined additively.
   *
   * Independent because one person can hold several of these at once — a manager who is also the
   * configured certification admin must see BOTH pages, not whichever a switch happened to test
   * first. Every consumer reads them from here so the sidebar cannot offer a page the page then
   * refuses.
   */
  canViewManagerQueue: boolean;
  canViewAdminQueue: boolean;
  canViewFinanceQueue: boolean;
  /**
   * Where the right comes from.
   *
   * 'admin'      an existing Finance/Super admin — they had it already
   * 'configured' the employee named in Settings → Certifications, and only for certifications
   * 'none'       no certification approval right
   */
  via: 'admin' | 'configured' | 'none';
  /** False until the configuration has been read, so a page can wait rather than flash. */
  ready: boolean;
}

export function useCertificationApproval(): CertificationApprovalAccess {
  const { user } = useAuth();
  const role = (user?.role as UserRole) || UserRole.EMPLOYEE;
  const email = user?.email;

  const [configuredEmail, setConfiguredEmail] = useState<string>('');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    loadCertificationSettings()
      .then((settings) => {
        if (active) setConfiguredEmail(settings.certificationAdminEmail);
      })
      .catch(() => {
        /*
         * A failed read grants nobody EXTRA, and revokes nobody.
         *
         * Leaving the configured email empty means canApproveCertifications falls back to the
         * role check alone: the existing admins carry on, and the additional approver is not
         * added on the strength of a value that could not be read. Failing closed on the grant
         * and open on the built-in right is the only combination that is safe both ways.
         */
        if (active) setConfiguredEmail('');
      })
      .finally(() => {
        if (active) setReady(true);
      });
    return () => {
      active = false;
    };
  }, []);

  const gates = {
    canViewManagerQueue: canViewCertificationApprovals(role),
    canViewAdminQueue: canViewCertificateAdminApproval(role, email, configuredEmail),
    canViewFinanceQueue: canViewFinanceReimbursements(role),
  };

  /*
   * `canApprove` is the ADMIN-stage decision right specifically, and it is what the admin page
   * uses to render Approve/Reject. It tracks the admin gate rather than being a fourth rule.
   */
  const canApprove = gates.canViewAdminQueue;

  /* 'admin' means they had the right by role and reach certifications through an admin area they
     already use; 'configured' means the grant came from Settings and covers certifications only,
     so their page has to stand on its own. The distinction is shown on screen. */
  const via: CertificationApprovalAccess['via'] = canApproveFinance(role)
    ? 'admin'
    : canApprove
    ? 'configured'
    : 'none';

  return { canApprove, ...gates, via, ready };
}
